import React, { useState } from 'react'
import {
  MapPin, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, Zap, Globe,
  BookOpen, ChevronRight
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'hacker',
    emoji: '🔐',
    title: '보안 전문가',
    category: '사이버보안',
    color: 'from-slate-600 to-slate-800',
    bg: 'bg-slate-50',
    border: 'border-slate-300',
    badge: 'bg-slate-100 text-slate-700',
    story: `중학교 1학년 민준이는 해킹 방어 동아리에 들었습니다.
선생님이 화면에 큰 숫자를 띄웠어요: 9,999,999,999,999,997
"이 숫자를 두 소수의 곱으로 나타낼 수 있을까?"
민준이는 처음에는 의아했지만, 소인수분해를 배우고 나서 깨달았습니다.
"이게 RSA 암호의 핵심이구나!" — 큰 합성수를 소인수분해하는 것이 인터넷 보안의 기초였습니다.`,
    situation: '소인수분해: 모든 합성수를 소수의 곱으로 유일하게 나타낼 수 있습니다.',
    question: '360을 소인수분해하고, 소인수 중 가장 큰 것을 구하세요.',
    formula: '360 = 2³ × 3² × 5',
    formulaDesc: '소인수분해 결과',
    factA: '소인수: 2, 3, 5',
    factADesc: '360의 소인수 (소수인 약수)',
    factB: '가장 큰 소인수: 5',
    factBDesc: 'GCD·LCM 계산의 기초',
    answer: '360 = 2³ × 3² × 5, 가장 큰 소인수 = 5',
    solutionSteps: [
      '2 | 360 → 2 | 180 → 2 | 90',
      '3 | 45 → 3 | 15 → 5 | 5 → 1',
      '360 = 2 × 2 × 2 × 3 × 3 × 5',
      '= 2³ × 3² × 5',
      '소인수: 2, 3, 5 → 가장 큰 소인수 = 5',
    ],
    whyMatters: 'RSA 암호는 "큰 합성수를 소인수분해하는 것은 엄청 어렵다"는 성질을 이용합니다. 인터넷 뱅킹, 쇼핑, 카카오톡 암호화 모두 소인수분해 원리!',
    quiz: [
      { q: '84를 소인수분해하면?', a: '2² × 3 × 7', hint: '2|84 → 2|42 → 3|21 → 7|7 → 1' },
      { q: '2³ × 3 × 5의 소인수 개수는?', a: '3개 (2, 3, 5)', hint: '소인수 = 소수인 약수' },
    ],
    insight: '네이버, 카카오, 삼성 코딩 테스트에서 소인수분해·소수 판별이 매년 출제됩니다!',
    jobs: ['사이버 보안 전문가', '암호학자', '정보보호 엔지니어', '보안 SW 개발자'],
  },
  {
    id: 'musician',
    emoji: '🎵',
    title: '작곡가 (K-pop)',
    category: '음악·예술',
    color: 'from-purple-500 to-indigo-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
    story: `중학교 1학년 소연이는 K-pop 작곡가를 꿈꿉니다.
DAW(작곡 프로그램)로 리듬을 만들다가 이상한 걸 발견했어요.
"4박자 루프랑 3박자 루프를 같이 틀면 왜 이상하게 들리지?"
선배 뮤지션이 답했습니다: "LCM(4, 3) = 12박 후에야 두 리듬이 다시 맞아떨어지거든!"
수학이 음악의 리듬을 설계한다는 걸 처음 알게 된 순간이었습니다.`,
    situation: '최소공배수(LCM): 두 리듬이 다시 만나는 박 수 = LCM(두 박자).',
    question: 'A 악기는 4박마다, B 악기는 6박마다 강조음이 납니다. 동시에 강조음이 나는 것은 몇 박 후인가요?',
    formula: 'LCM(4, 6) = 12',
    formulaDesc: '두 리듬이 만나는 최소 주기',
    factA: '4 = 2²,  6 = 2 × 3',
    factADesc: '소인수분해로 LCM 계산',
    factB: 'LCM = 2² × 3 = 12박',
    factBDesc: '12박 후 동시에 강조음 발생',
    answer: 'LCM(4, 6) = 12 → 12박 후 동시에 강조음',
    solutionSteps: [
      '4 = 2²,  6 = 2 × 3',
      'LCM: 각 소인수의 최대 지수',
      '= 2² × 3 = 12',
      '12박 후 처음으로 동시에 강조음 발생',
    ],
    whyMatters: '음악의 박자, 리듬 루프, 화음 주기는 모두 LCM으로 설명됩니다. 작곡가들은 이 수학적 구조를 이용해 음악을 설계합니다.',
    quiz: [
      { q: 'LCM(8, 12)는 얼마인가요?', a: '24', hint: '8=2³, 12=2²×3 → LCM=2³×3=24' },
      { q: '3박과 5박이 다시 맞아떨어지는 박 수는?', a: '15박', hint: 'LCM(3,5)=15 (서로소)' },
    ],
    insight: 'K-pop 프로듀서, 게임 BGM 작곡가, 영화 음악 감독 모두 LCM으로 리듬 구조를 설계합니다!',
    jobs: ['음악 프로듀서', '작곡가', '사운드 디자이너', 'DAW 개발자'],
  },
  {
    id: 'architect',
    emoji: '🏗️',
    title: '인테리어 디자이너',
    category: '건축·디자인',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `인테리어 디자이너 지수씨는 첫 독립 프로젝트를 맡았습니다.
욕실 바닥: 가로 120cm, 세로 80cm.
"타일을 어떻게 붙여야 잘리는 타일 없이 딱 맞지?"
선배가 한마디: "GCD(120, 80) 구해봐. 그게 타일 최대 크기야!"
GCD = 40cm → 40×40cm 타일 6장으로 완벽하게 마무리했습니다.`,
    situation: '최대공약수(GCD): 두 변을 동시에 나누는 가장 큰 수 = 타일 최대 크기.',
    question: 'GCD(120, 80)을 소인수분해로 구하고, 필요한 타일 수를 계산하세요.',
    formula: 'GCD(120, 80) = 40',
    formulaDesc: '잘림 없이 쓸 수 있는 타일 최대 크기',
    factA: '120 = 2³×3×5,  80 = 2⁴×5',
    factADesc: '소인수분해로 GCD 계산',
    factB: '타일 수: (120÷40) × (80÷40) = 3 × 2 = 6장',
    factBDesc: '최소 비용으로 타일 배치 완성',
    answer: 'GCD(120, 80) = 40 → 40×40cm 타일 6장',
    solutionSteps: [
      '120 = 2³ × 3 × 5',
      '80 = 2⁴ × 5',
      'GCD: 공통 소인수의 최소 지수',
      '= 2³ × 5 = 8 × 5 = 40',
      '타일 수: (120÷40) × (80÷40) = 3 × 2 = 6장',
    ],
    whyMatters: '건축·인테리어에서 타일, 벽돌, 창문 배치 모두 GCD가 필요합니다. AutoCAD, SketchUp 같은 설계 소프트웨어도 내부적으로 GCD를 사용합니다.',
    quiz: [
      { q: '가로 72cm, 세로 48cm 공간의 최대 정사각형 타일 한 변은?', a: '24cm (GCD=24)', hint: '72=2³×3², 48=2⁴×3 → GCD=2³×3=24' },
      { q: '그 타일을 몇 장 써야 하나요?', a: '6장 (3×2)', hint: '72÷24=3, 48÷24=2 → 3×2=6' },
    ],
    insight: '건축가, 도시계획가, 게임 맵 디자이너 모두 GCD로 최적 배치를 설계합니다!',
    jobs: ['건축가', '인테리어 디자이너', 'CAD 엔지니어', '도시 계획가'],
  },
  {
    id: 'pharmacist',
    emoji: '💊',
    title: '병원 약사',
    category: '의약·보건',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `병원 약사 현우씨는 환자 A의 복약 기록을 봤습니다.
혈압약은 6시간마다, 당뇨약은 8시간마다 복용해야 합니다.
"두 약이 겹쳐서 먹으면 부작용이 생길 수 있어. 정확히 계산해야 해."
LCM(6, 8) = 24시간 → 딱 하루 뒤에 두 약이 동시 복용됩니다.
수학으로 환자의 안전을 지킨 순간이었습니다.`,
    situation: '최소공배수(LCM): 두 복약 주기가 겹치는 최소 시간 = LCM(주기1, 주기2).',
    question: '6시간마다, 8시간마다 복용하는 두 약이 처음 동시에 복용되는 시간은?',
    formula: 'LCM(6, 8) = 24',
    formulaDesc: '두 약이 처음 겹치는 시간 (시간)',
    factA: '6 = 2 × 3,  8 = 2³',
    factADesc: '소인수분해로 LCM 계산',
    factB: 'LCM = 2³ × 3 = 24시간 (= 하루)',
    factBDesc: '딱 하루 뒤에 두 약이 동시 복용',
    answer: 'LCM(6, 8) = 24시간 후 (하루 뒤)',
    solutionSteps: [
      '6 = 2 × 3',
      '8 = 2³',
      'LCM: 각 소인수의 최대 지수',
      '= 2³ × 3 = 8 × 3 = 24',
      '24시간(하루) 후 두 약이 처음으로 동시 복용',
    ],
    whyMatters: '약물 복용 주기, 의료 장비 점검 주기, 수술 일정 조율 등 의료 현장의 많은 계산이 LCM을 활용합니다.',
    quiz: [
      { q: '4시간마다와 6시간마다 복용할 때 다시 겹치는 시간은?', a: '12시간 후', hint: 'LCM(4,6): 4=2², 6=2×3 → LCM=2²×3=12' },
      { q: 'LCM(12, 18)은?', a: '36', hint: '12=2²×3, 18=2×3² → LCM=2²×3²=36' },
    ],
    insight: '의사, 약사, 임상 연구원 등 의료 전문가들은 LCM으로 치료 일정을 과학적으로 설계합니다!',
    jobs: ['약사', '의사', '임상 약학 연구원', '의료 AI 개발자'],
  },
  {
    id: 'developer',
    emoji: '💻',
    title: '게임 개발자',
    category: 'IT·개발',
    color: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    story: `중학교 1학년 서준이는 자신만의 RPG 게임을 만들고 있습니다.
"슬라임은 5초마다, 고블린은 8초마다, 드래곤은 12초마다 나와야 해.
세 마리가 동시에 나오는 건 언제지?"
LCM(5, 8, 12) = 120초 → 2분마다 동시 소환!
게임 밸런스를 수학으로 설계한 서준이의 게임은 프로그래밍 대회에서 대상을 받았습니다.`,
    situation: 'LCM(세 수): 세 몬스터 소환 주기가 처음으로 겹치는 시간 = LCM(5, 8, 12).',
    question: 'LCM(5, 8, 12)를 소인수분해로 구하세요.',
    formula: 'LCM(5, 8, 12) = 120',
    formulaDesc: '세 몬스터가 동시 소환되는 주기 (초)',
    factA: '5=5, 8=2³, 12=2²×3',
    factADesc: '소인수분해로 LCM 계산',
    factB: 'LCM = 2³ × 3 × 5 = 120초',
    factBDesc: '코드: if (time % 120 === 0) spawnAll()',
    answer: 'LCM(5, 8, 12) = 2³ × 3 × 5 = 120초',
    solutionSteps: [
      '5 = 5',
      '8 = 2³',
      '12 = 2² × 3',
      'LCM: 각 소인수의 최대 지수',
      '= 2³ × 3 × 5 = 8 × 3 × 5 = 120초',
    ],
    whyMatters: '게임 AI, 이벤트 타이밍, 애니메이션 루프 주기 계산에 LCM이 직접 사용됩니다. 네이버·카카오·삼성 코딩 테스트에서 매년 출제!',
    quiz: [
      { q: 'LCM(4, 6, 10)을 구하세요.', a: '60', hint: '4=2², 6=2×3, 10=2×5 → LCM=2²×3×5=60' },
      { q: 'GCD(60, 90)을 구하세요.', a: '30', hint: '60=2²×3×5, 90=2×3²×5 → GCD=2×3×5=30' },
    ],
    insight: '카카오, 네이버, 삼성 SW 역량테스트에서 소인수분해·GCD·LCM이 필수 유형입니다!',
    jobs: ['게임 개발자', '알고리즘 엔지니어', '백엔드 개발자', '데이터 사이언티스트'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  { icon: '🔐', title: '사이버 보안 전문가', example: 'RSA 암호 = 큰 합성수 소인수분해의 어려움 활용', formula: 'N = p × q (p, q: 큰 소수)', salary: '연 6,000만~1억원+', color: 'from-slate-500 to-slate-700' },
  { icon: '🏗️', title: '건축·인테리어 디자이너', example: '타일·창문·벽돌 배치 최적화', formula: 'tile_size = GCD(width, height)', salary: '연 3,500만~7,000만원', color: 'from-orange-500 to-amber-500' },
  { icon: '🎵', title: '음악 프로듀서', example: '리듬 루프 주기, 박자 구조 설계', formula: 'sync_beat = LCM(beat_A, beat_B)', salary: '연 3,000만~억대', color: 'from-purple-500 to-indigo-500' },
  { icon: '💊', title: '약사·의사', example: '약물 복용 주기, 치료 스케줄 조율', formula: 'conflict_time = LCM(period_A, period_B)', salary: '연 7,000만~2억원+', color: 'from-emerald-500 to-teal-500' },
  { icon: '💻', title: '게임 개발자', example: '이벤트 타이밍, 몬스터 소환 주기', formula: 'if (time % LCM(...) === 0) spawnAll()', salary: '연 4,000만~1억원', color: 'from-blue-500 to-indigo-500' },
  { icon: '🏦', title: '금융·회계 전문가', example: '대출 상환 주기, 복리 계산', formula: 'payment_cycle = LCM(month, quarter)', salary: '연 4,500만~1억원', color: 'from-green-500 to-emerald-500' },
]

// ── 도전 문제 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1', emoji: '🏠', level: '⭐ 쉬움', levelColor: 'bg-emerald-100 text-emerald-700',
    question: '가로 96cm, 세로 64cm 벽을 정사각형 타일로 빈틈 없이 채우려고 합니다. 타일의 최대 크기(한 변)는?',
    hint: 'GCD(96, 64)를 소인수분해로 구하세요.',
    answer: '32cm', explanation: '96=2⁵×3, 64=2⁶ → GCD=2⁵=32. 타일 한 변 = 32cm',
  },
  {
    id: 'c2', emoji: '🚦', level: '⭐⭐ 보통', levelColor: 'bg-amber-100 text-amber-700',
    question: '신호등 A는 30초마다, B는 45초마다, C는 60초마다 바뀝니다. 세 신호등이 동시에 바뀌는 것은 몇 초 후인가요?',
    hint: 'LCM(30, 45, 60)을 소인수분해로 구하세요.',
    answer: '180초 (3분)', explanation: '30=2×3×5, 45=3²×5, 60=2²×3×5 → LCM=2²×3²×5=180초',
  },
  {
    id: 'c3', emoji: '🔢', level: '⭐⭐⭐ 심화', levelColor: 'bg-red-100 text-red-700',
    question: '두 자연수 A, B의 GCD=12, LCM=180입니다. A=36일 때 B는?',
    hint: 'GCD × LCM = A × B 공식을 이용하세요.',
    answer: 'B = 60', explanation: '12 × 180 = 36 × B → B = 2160 ÷ 36 = 60',
  },
  {
    id: 'c4', emoji: '🎵', level: '⭐⭐⭐ 심화', levelColor: 'bg-red-100 text-red-700',
    question: '4박, 6박, 9박 루프가 동시에 시작됩니다. 다시 동시에 시작되는 박 수는? 그때까지 각 루프는 몇 번 반복되나요?',
    hint: 'LCM(4, 6, 9)를 구한 후 각각 나눠보세요.',
    answer: '36박 후: 4박 9회, 6박 6회, 9박 4회', explanation: '4=2², 6=2×3, 9=3² → LCM=2²×3²=36',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '사이버보안': 'bg-slate-100 text-slate-700',
  '음악·예술': 'bg-purple-100 text-purple-700',
  '건축·디자인': 'bg-amber-100 text-amber-700',
  '의약·보건': 'bg-emerald-100 text-emerald-700',
  'IT·개발': 'bg-blue-100 text-blue-700',
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: { q: string; a: string; hint: string }[] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})
  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1">
              <Lightbulb size={11} /> 힌트
            </button>
            <button onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors">
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">💡 힌트: <span className="font-mono">{q.hint}</span></div>}
          {revealed[i] && <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"><span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span></div>}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 ───────────────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)
  return (
    <div className="card border border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{ch.emoji}</span>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1">
          <Lightbulb size={11} /> 힌트
        </button>
        <button onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors flex items-center gap-1">
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">💡 {ch.hint}</div>}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function N1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers',   label: '직업과 소인수분해', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 소인수분해</h1>
            <p className="text-sm text-slate-500">보안전문가, 작곡가, 건축가, 약사, 개발자 — 모두 GCD·LCM을 씁니다!</p>
          </div>
        </div>
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 소인수분해가 세상을 움직인다!</p>
          <p className="text-sm opacity-90 mb-3">GCD로 타일을 설계하고, LCM으로 리듬을 만들고, 소인수분해로 인터넷을 지킵니다.</p>
          <div className="grid grid-cols-5 gap-2">
            {[{ icon: '🔐', job: '보안' }, { icon: '🎵', job: '작곡' }, { icon: '🏗️', job: '건축' }, { icon: '💊', job: '의약' }, { icon: '💻', job: 'IT' }].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'
            }`}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: 시나리오 */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat ? 'bg-primary-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scenario Cards */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button key={scenario.id} onClick={() => setSelected(scenario)}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category] ?? 'bg-slate-100 text-slate-600'}`}>{scenario.category}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-primary-600 font-semibold">{scenario.formula}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-2 space-y-4">
              {/* Situation Card */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category] ?? 'bg-slate-100 text-slate-600'}`}>{selected.category}</span>
                  </div>
                </div>
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 소인수분해</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>
                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 수식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="text-2xl font-black text-primary-700 font-mono">{selected.formula}</span>
                    <span className="text-xs text-slate-500">{selected.formulaDesc}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold font-mono">{selected.factA}</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.factADesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold font-mono">{selected.factB}</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.factBDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Q&A */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <button onClick={() => setShowTable(!showTable)}
                  className="mt-2 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {showTable ? '풀이 숨기기' : '단계별 풀이 보기'}
                </button>
                {showTable && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-amber-700 mb-3">📝 단계별 풀이</p>
                    <div className="space-y-1.5">
                      {selected.solutionSteps.map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${i === selected.solutionSteps.length - 1 ? 'bg-emerald-500 text-white' : 'bg-amber-200 text-amber-700'}`}>{i + 1}</span>
                          <span className={`text-sm font-mono ${i === selected.solutionSteps.length - 1 ? 'font-black text-emerald-700 text-base' : 'text-amber-800'}`}>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700">✅ 정답: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700"><span className="font-semibold">왜 중요한가?</span> {selected.whyMatters}</p>
                </div>
              </div>

              {/* Quiz */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* Jobs */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-primary-500" /> 이 수학을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* Insight */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2"><Zap size={15} className="text-yellow-400" /> 실제로 어디서 쓰이나요?</p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['소인수분해', 'GCD = 공약수', 'LCM = 공배수', '코딩 테스트 필수'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-8 card bg-gradient-to-br from-primary-50 to-accent-50 border-primary-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 소인수분해 실생활 핵심 패턴</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔑', title: 'GCD = 최대 공통 단위', desc: '타일 크기, 가장 큰 공통 약수 → "얼마나 크게 나눌 수 있나?"', color: 'text-orange-600' },
                { icon: '🔄', title: 'LCM = 최소 공통 주기', desc: '리듬 주기, 약 복용 간격 → "언제 다시 동시에 만나나?"', color: 'text-blue-600' },
                { icon: '🛡️', title: '소수 = 암호의 기초', desc: '큰 소수의 곱 → 소인수분해가 어려울수록 암호가 강해짐!', color: 'text-emerald-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* TAB 2: 직업 탐색 */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 소인수분해·GCD·LCM을 쓰는 직업들</h2>
            <p className="text-sm text-violet-100">소인수분해는 수학의 기초인 동시에 보안·건축·음악·의료·IT의 핵심 도구입니다.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>{career.icon}</div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-primary-700 font-bold">{career.formula}</code>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">💰 {career.salary}</span>
              </div>
            ))}
          </div>
          <div className="card border border-primary-200 bg-primary-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><ChevronRight size={18} className="text-primary-600" /> 소인수분해에서 시작하는 학습 경로</h3>
            <div className="flex flex-wrap items-center gap-2">
              {[{ step: '중1', topic: '소인수분해', active: true }, { step: '중1', topic: 'GCD·LCM', active: true }, { step: '중2', topic: '정수론', active: false }, { step: '고등', topic: '정수와 나눗셈', active: false }, { step: '대학', topic: '추상대수학', active: false }, { step: '직업', topic: '암호학·알고리즘', active: false }].map((item, i) => (
                <React.Fragment key={i}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-primary-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 5 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: 도전 문제 */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-amber-100">실제 상황에서 GCD·LCM을 직접 적용해보세요. 소인수분해로 풀어야 합니다!</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => <ChallengeCard key={ch.id} ch={ch} />)}
          </div>
          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Star size={16} className="text-amber-500" /> 실생활 문제 풀기 비법</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 공약수 문제', desc: '"가장 크게 나눠라" → GCD!', example: '타일 최대 크기 = GCD(가로, 세로)', color: 'bg-orange-50 border-orange-200 text-orange-800' },
                { step: '② 공배수 문제', desc: '"언제 다시 만나나" → LCM!', example: '동시 발생 주기 = LCM(주기1, 주기2)', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                { step: '③ 소인수분해', desc: '먼저 소인수분해 → GCD·LCM 계산', example: 'GCD: 공통 소인수 × 최소지수', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1"><p className="text-xs font-mono font-bold">{item.example}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
